import {Event} from '@/apiTypes/event';
import {EventFilter} from '@/apiTypes/eventFilter';
import {CreateEventListInput} from '@/apiTypes/eventList';
import type {
  EventListDetail,
  EventListSummary,
  PublicEventListDetail,
} from '@/apiTypes/eventList';
import {EventListItemInput} from '@/apiTypes/savedEvent';
import {authMiddleware, optionalAuthMiddleware} from '@/helpers/authMiddleware';
import {fetchEventsByEventFilter} from '@/helpers/eventFilter.server';
import {extractRequestContext, trackEvent} from '@/helpers/mixpanel.server';
import {prismaClient} from '@/helpers/prismaClient.server';
import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';
import {z} from 'zod';

export const SAVED_EVENTS_LIST_NAME = 'Saved Events';

/** Prisma where filter for non-archived events that haven't ended yet. */
function futureEventFilter(now: Date) {
  return {
    archived: false,
    OR: [
      {endDate: {gte: now}},
      {endDate: null, startDate: {gte: now}},
      {endDate: null, startDate: null},
    ],
  };
}

/** Lightweight include for event card display — matches search pattern */
const eventListItemInclude = {
  routes: {
    select: {
      id: true,
      createdAt: true,
      updatedAt: true,
      name: true,
      description: true,
      distance_meters: true,
      elevation_gain_meters: true,
      routeFileUrl: true,
      eventId: true,
    },
  },
  categories: {
    include: {
      prices: true,
    },
  },
} as const;

/**
 * Finds or creates the "Saved Events" list for a user.
 * Used by save/unsave event flow.
 */
async function findOrCreateSavedEventsList(
  db: Awaited<ReturnType<typeof prismaClient>>,
  profileId: string
) {
  const existing = await db.eventList.findFirst({
    where: {profileId, name: SAVED_EVENTS_LIST_NAME},
  });
  if (existing) return existing;

  return db.eventList.create({
    data: {
      name: SAVED_EVENTS_LIST_NAME,
      visibility: 'PRIVATE',
      profileId,
    },
  });
}

/**
 * Creates a new event list for the authenticated user.
 * The name "Saved Events" is reserved.
 */
export const createEventList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof CreateEventListInput> => {
    const result = CreateEventListInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;

    if (data.name === SAVED_EVENTS_LIST_NAME) {
      throw new Error(`"${SAVED_EVENTS_LIST_NAME}" is a reserved list name`);
    }

    const db = await prismaClient();

    const list = await db.eventList.create({
      data: {
        name: data.name,
        visibility: data.visibility,
        profileId: profile.id,
      },
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'List Created',
      {list_id: list.id, list_name: list.name, visibility: data.visibility},
      requestContext
    );

    return {id: list.id, name: list.name};
  });

/**
 * Deletes an event list owned by the authenticated user.
 * The "Saved Events" list cannot be deleted.
 */
export const deleteEventList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid list ID');
    }
    return result.data;
  })
  .handler(async ({data: listId, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const list = await db.eventList.findUnique({
      where: {id: listId},
    });

    if (!list) throw new Error('List not found');
    if (list.profileId !== profile.id) throw new Error('List not found');
    if (list.name === SAVED_EVENTS_LIST_NAME) {
      throw new Error('The "Saved Events" list cannot be deleted');
    }

    await db.eventList.delete({where: {id: listId}});
    return {success: true};
  });

/**
 * Returns the authenticated user's event lists with item counts.
 * Always includes a "Saved Events" entry — virtual if no DB row exists.
 */
export const getUserEventLists = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .handler(async ({context}): Promise<EventListSummary[]> => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const lists = await db.eventList.findMany({
      where: {profileId: profile.id},
      orderBy: {createdAt: 'asc'},
      include: {_count: {select: {items: true, followers: true, subscriptions: true}}},
    });

    const summaries: EventListSummary[] = lists.map((list) => ({
      // Use virtual 'saved' ID for the Saved Events list so it matches
      // the ID returned by getEventListMembership
      id: list.name === SAVED_EVENTS_LIST_NAME ? 'saved' : list.id,
      name: list.name,
      description: list.description,
      coverImageUrl: list.coverImageUrl,
      itemCount: list._count.items,
      visibility: list.visibility,
      createdAt: list.createdAt,
      followerCount: list._count.followers + list._count.subscriptions,
    }));

    const hasSavedList = summaries.some(
      (s) => s.name === SAVED_EVENTS_LIST_NAME
    );
    if (!hasSavedList) {
      summaries.unshift({
        id: 'saved',
        name: SAVED_EVENTS_LIST_NAME,
        description: null,
        coverImageUrl: null,
        itemCount: 0,
        visibility: 'PRIVATE',
        createdAt: new Date(),
        followerCount: 0,
      });
    }

    return summaries;
  });

const GetEventListByIdInput = z.object({
  listId: z.union([z.literal('saved'), z.string().uuid()]),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(50).default(12),
});

/**
 * Returns a single event list with paginated event items.
 * For virtual "saved" list (no DB row), returns empty events.
 * Events ordered by soonest startDate first, past events excluded.
 */
export const getEventListById = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown) => {
    const result = GetEventListByIdInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}): Promise<EventListDetail> => {
    const {profile} = context.auth;
    const db = await prismaClient();

    // Resolve the list — virtual 'saved' ID maps to the Saved Events DB row
    const list =
      data.listId === 'saved'
        ? await db.eventList.findFirst({
            where: {profileId: profile.id, name: SAVED_EVENTS_LIST_NAME},
          })
        : await db.eventList.findUnique({
            where: {id: data.listId},
          });

    // No DB row yet for Saved Events — return empty list
    if (!list && data.listId === 'saved') {
      return {
        list: {
          id: 'saved',
          name: SAVED_EVENTS_LIST_NAME,
          description: null,
          coverImageUrl: null,
          visibility: 'PRIVATE',
          kind: 'MANUAL',
          createdAt: new Date(),
        },
        events: [],
        pagination: {
          page: 1,
          pageSize: data.pageSize,
          totalCount: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    if (!list || list.profileId !== profile.id) {
      throw new Error('List not found');
    }

    const now = new Date();
    const skip = (data.page - 1) * data.pageSize;

    let events: z.infer<typeof Event>[];
    let totalCount: number;
    if (list.kind === 'SMART') {
      if (!list.filter) {
        events = [];
        totalCount = 0;
      } else {
        const filter = EventFilter.parse(list.filter);
        const result = await fetchEventsByEventFilter({
          filter,
          skip,
          take: data.pageSize,
          now,
          withCount: true,
        });
        events = z.array(Event).parse(result.events);
        totalCount = result.totalCount ?? 0;
      }
    } else {
      const where = {
        eventListId: list.id,
        event: futureEventFilter(now),
      };
      const [items, count] = await db.$transaction([
        db.eventListItem.findMany({
          where,
          skip,
          take: data.pageSize,
          orderBy: {event: {startDate: {sort: 'asc', nulls: 'last'}}},
          include: {
            event: {
              include: eventListItemInclude,
            },
          },
        }),
        db.eventListItem.count({where}),
      ]);
      events = z.array(Event).parse(items.map((item) => item.event));
      totalCount = count;
    }
    const totalPages = Math.ceil(totalCount / data.pageSize);

    return {
      list: {
        id: list.id,
        name: list.name,
        description: list.description,
        coverImageUrl: list.coverImageUrl,
        visibility: list.visibility,
        kind: list.kind,
        createdAt: list.createdAt,
      },
      events,
      pagination: {
        page: data.page,
        pageSize: data.pageSize,
        totalCount,
        totalPages,
        hasNextPage: data.page < totalPages,
        hasPreviousPage: data.page > 1,
      },
    };
  });

const GetPublicEventListInput = z.object({
  profileId: z.string().uuid(),
  listId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(50).default(12),
});

/**
 * Returns a public event list with paginated events.
 * Throws when the list is missing, the profileId does not match the
 * list owner, or the list is private and the viewer is not its owner.
 * Errors are caught by the route's errorComponent and rendered as a 404.
 */
export const getPublicEventList = createServerFn({method: 'GET'})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown) => {
    const result = GetPublicEventListInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}): Promise<PublicEventListDetail> => {
    const viewer = context.auth;
    const db = await prismaClient();

    const list = await db.eventList.findUnique({
      where: {id: data.listId},
      include: {
        profile: {
          select: {
            id: true,
            displayName: true,
            firstName: true,
            lastName: true,
            avatarUrl: true,
          },
        },
        _count: {select: {followers: true, subscriptions: true}},
      },
    });

    const viewerProfileId = viewer?.profile.id ?? null;
    const isOwner =
      viewerProfileId !== null && viewerProfileId === list?.profileId;

    if (
      !list ||
      list.profileId !== data.profileId ||
      (list.visibility === 'PRIVATE' && !isOwner)
    ) {
      throw new Error('List not found');
    }

    const isFollowing = viewerProfileId
      ? Boolean(
          await db.listFollower.findFirst({
            where: {profileId: viewerProfileId, eventListId: list.id},
            select: {id: true},
          })
        )
      : false;

    const now = new Date();
    const skip = (data.page - 1) * data.pageSize;

    let events: z.infer<typeof Event>[];
    let totalCount: number;
    if (list.kind === 'SMART') {
      if (!list.filter) {
        events = [];
        totalCount = 0;
      } else {
        const filter = EventFilter.parse(list.filter);
        const result = await fetchEventsByEventFilter({
          filter,
          skip,
          take: data.pageSize,
          now,
          withCount: true,
        });
        events = z.array(Event).parse(result.events);
        totalCount = result.totalCount ?? 0;
      }
    } else {
      const where = {
        eventListId: list.id,
        event: futureEventFilter(now),
      };
      const [items, count] = await db.$transaction([
        db.eventListItem.findMany({
          where,
          skip,
          take: data.pageSize,
          orderBy: {event: {startDate: {sort: 'asc', nulls: 'last'}}},
          include: {event: {include: eventListItemInclude}},
        }),
        db.eventListItem.count({where}),
      ]);
      events = z.array(Event).parse(items.map((item) => item.event));
      totalCount = count;
    }
    const totalPages = Math.max(1, Math.ceil(totalCount / data.pageSize));

    const visibility: 'PUBLIC' | 'PRIVATE' =
      list.visibility === 'PUBLIC' ? 'PUBLIC' : 'PRIVATE';

    return {
      list: {
        id: list.id,
        name: list.name,
        description: list.description,
        coverImageUrl: list.coverImageUrl,
        visibility,
        kind: list.kind,
        createdAt: list.createdAt,
      },
      creator: {
        id: list.profile.id,
        displayName: list.profile.displayName,
        firstName: list.profile.firstName,
        lastName: list.profile.lastName,
        avatarUrl: list.profile.avatarUrl,
      },
      events,
      pagination: {
        page: data.page,
        pageSize: data.pageSize,
        totalCount,
        totalPages,
        hasNextPage: data.page < totalPages,
        hasPreviousPage: data.page > 1,
      },
      isOwner,
      followerCount: list._count.followers + list._count.subscriptions,
      isFollowing,
    };
  });

/**
 * Adds an event to a specific list. For the virtual "saved" list,
 * delegates to the Saved Events flow. Idempotent.
 */
export const addEventToList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof EventListItemInput> => {
    const result = EventListItemInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const event = await db.event.findUnique({
      where: {id: data.eventId},
      select: {id: true},
    });
    if (!event) throw new Error('Event not found');

    if (data.listId === 'saved') {
      const savedList = await findOrCreateSavedEventsList(db, profile.id);
      await db.eventListItem.upsert({
        where: {
          eventListId_eventId: {
            eventListId: savedList.id,
            eventId: data.eventId,
          },
        },
        create: {eventListId: savedList.id, eventId: data.eventId},
        update: {},
      });

      const request = getRequest();
      const requestContext = request ? extractRequestContext(request) : undefined;
      await trackEvent(
        profile.id,
        'Event Saved',
        {event_id: data.eventId, list_id: savedList.id, list_name: SAVED_EVENTS_LIST_NAME},
        requestContext
      );
      return {success: true};
    }

    const list = await db.eventList.findUnique({where: {id: data.listId}});
    if (!list || list.profileId !== profile.id) {
      throw new Error('List not found');
    }

    await db.eventListItem.upsert({
      where: {
        eventListId_eventId: {
          eventListId: data.listId,
          eventId: data.eventId,
        },
      },
      create: {eventListId: data.listId, eventId: data.eventId},
      update: {},
    });

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'Event Saved',
      {event_id: data.eventId, list_id: data.listId, list_name: list.name},
      requestContext
    );

    return {success: true};
  });

/**
 * Removes an event from a specific list. For the virtual "saved" list,
 * removes from the Saved Events list. No error if not present.
 */
export const removeEventFromList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof EventListItemInput> => {
    const result = EventListItemInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    if (data.listId === 'saved') {
      const savedList = await db.eventList.findFirst({
        where: {profileId: profile.id, name: SAVED_EVENTS_LIST_NAME},
      });
      if (savedList) {
        await db.eventListItem.deleteMany({
          where: {eventListId: savedList.id, eventId: data.eventId},
        });
      }

      return {success: true};
    }

    const list = await db.eventList.findUnique({where: {id: data.listId}});
    if (!list || list.profileId !== profile.id) {
      throw new Error('List not found');
    }

    await db.eventListItem.deleteMany({
      where: {eventListId: data.listId, eventId: data.eventId},
    });

    return {success: true};
  });

/** Return type for getEventListForEdit — list metadata + (for smart
 * lists) the tag-name lookup needed to hydrate the builder chips. */
interface EventListEditData {
  id: string;
  name: string;
  description: string | null;
  coverImageUrl: string | null;
  visibility: 'PRIVATE' | 'PUBLIC';
  kind: 'MANUAL' | 'SMART';
  filter: unknown;
  /** id → name for every tag id referenced in the filter; empty for
   * MANUAL lists. */
  tagNamesById: Record<string, string>;
  /** id → name for every organizer id referenced in the filter; empty
   * for MANUAL lists. */
  organizerNamesById: Record<string, string>;
  /** Events in the list — populated for MANUAL lists, empty for SMART. */
  items: Array<{
    id: string;
    name: string;
    startDate: Date | null;
    endDate: Date | null;
    location: string | null;
    eventType: string;
    discipline: string;
    heroUrl: string | null;
  }>;
}

/**
 * Fetches event list metadata for the edit view.
 * Requires ownership. Rejects the "Saved Events" system list.
 */
export const getEventListForEdit = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid list ID');
    return result.data;
  })
  .handler(async ({data: listId, context}): Promise<EventListEditData> => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const list = await db.eventList.findUnique({
      where: {id: listId},
    });

    if (!list || list.profileId !== profile.id) {
      throw new Error('List not found');
    }
    if (list.name === SAVED_EVENTS_LIST_NAME) {
      throw new Error('The "Saved Events" list cannot be edited');
    }

    let tagNamesById: Record<string, string> = {};
    let organizerNamesById: Record<string, string> = {};
    if (list.kind === 'SMART' && list.filter) {
      const parsed = EventFilter.safeParse(list.filter);
      if (parsed.success) {
        const tagIds = new Set<string>();
        for (const c of parsed.data.criteria) {
          if (c.field === 'tags') {
            c.includesAll?.forEach((id) => tagIds.add(id));
            c.includesAny?.forEach((id) => tagIds.add(id));
          }
        }
        if (tagIds.size > 0) {
          const rows = await db.eventTag.findMany({
            where: {id: {in: Array.from(tagIds)}},
            select: {id: true, name: true},
          });
          tagNamesById = Object.fromEntries(rows.map((r) => [r.id, r.name]));
        }

        const organizerIds = new Set<string>();
        for (const c of parsed.data.criteria) {
          if (c.field === 'organizer') {
            c.organizerIds.forEach((id) => organizerIds.add(id));
          }
        }
        if (organizerIds.size > 0) {
          const orgRows = await db.organizer.findMany({
            where: {id: {in: Array.from(organizerIds)}},
            select: {id: true, name: true},
          });
          organizerNamesById = Object.fromEntries(
            orgRows.map((r) => [r.id, r.name])
          );
        }
      }
    }

    let items: EventListEditData['items'] = [];
    if (list.kind === 'MANUAL') {
      const listItems = await db.eventListItem.findMany({
        where: {eventListId: list.id},
        orderBy: {event: {startDate: {sort: 'asc', nulls: 'last'}}},
        include: {
          event: {
            select: {
              id: true,
              name: true,
              startDate: true,
              endDate: true,
              location: true,
              eventType: true,
              discipline: true,
              heroUrl: true,
            },
          },
        },
      });
      items = listItems.map((li) => li.event);
    }

    return {
      id: list.id,
      name: list.name,
      description: list.description,
      coverImageUrl: list.coverImageUrl,
      visibility: list.visibility === 'PUBLIC' ? 'PUBLIC' : 'PRIVATE',
      kind: list.kind,
      filter: list.filter,
      tagNamesById,
      organizerNamesById,
      items,
    };
  });

const UpdateEventListServerInput = z.object({
  listId: z.string().uuid(),
  name: z.string().min(1).max(100).optional(),
  description: z.string().nullable().optional(),
  coverImageUrl: z.string().nullable().optional(),
  visibility: z.enum(['PRIVATE', 'PUBLIC']).optional(),
});

/**
 * Updates event list metadata. Only provided fields are changed.
 * Requires ownership. Rejects the "Saved Events" system list.
 */
export const updateEventList = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof UpdateEventListServerInput> => {
      const result = UpdateEventListServerInput.safeParse(data);
      if (!result.success) {
        throw new Error(
          result.error.errors.map((e) => e.message).join(', ')
        );
      }
      return result.data;
    }
  )
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const list = await db.eventList.findUnique({
      where: {id: data.listId},
    });

    if (!list || list.profileId !== profile.id) {
      throw new Error('List not found');
    }
    if (list.name === SAVED_EVENTS_LIST_NAME) {
      throw new Error('The "Saved Events" list cannot be edited');
    }
    if (data.name === SAVED_EVENTS_LIST_NAME) {
      throw new Error(`"${SAVED_EVENTS_LIST_NAME}" is a reserved list name`);
    }

    const updateFields: {
      name?: string;
      description?: string | null;
      coverImageUrl?: string | null;
      visibility?: 'PRIVATE' | 'PUBLIC';
    } = {};

    if (data.name !== undefined) updateFields.name = data.name;
    if (data.description !== undefined)
      updateFields.description = data.description;
    if (data.coverImageUrl !== undefined)
      updateFields.coverImageUrl = data.coverImageUrl;
    if (data.visibility !== undefined) updateFields.visibility = data.visibility;

    if (Object.keys(updateFields).length === 0) {
      throw new Error('At least one field must be provided to update');
    }

    if (data.visibility === 'PRIVATE') {
      const [updated] = await db.$transaction([
        db.eventList.update({
          where: {id: data.listId},
          data: updateFields,
          select: {
            id: true,
            name: true,
            description: true,
            coverImageUrl: true,
            visibility: true,
          },
        }),
        db.listFollower.deleteMany({
          where: {eventListId: data.listId},
        }),
      ]);
      return updated;
    }

    return await db.eventList.update({
      where: {id: data.listId},
      data: updateFields,
      select: {
        id: true,
        name: true,
        description: true,
        coverImageUrl: true,
        visibility: true,
      },
    });
  });

const UpdateManualListEventsInput = z.object({
  listId: z.string().uuid(),
  addEventIds: z.array(z.string().uuid()),
  removeEventIds: z.array(z.string().uuid()),
});

/**
 * Batch-updates the events in a manual list. Adds and removes are
 * applied atomically in a single transaction.
 */
export const updateManualListEvents = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof UpdateManualListEventsInput> => {
      const result = UpdateManualListEventsInput.safeParse(data);
      if (!result.success) {
        throw new Error(
          result.error.errors.map((e) => e.message).join(', ')
        );
      }
      return result.data;
    }
  )
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const list = await db.eventList.findUnique({
      where: {id: data.listId},
    });

    if (!list || list.profileId !== profile.id) {
      throw new Error('List not found');
    }
    if (list.kind !== 'MANUAL') {
      throw new Error('Cannot update events on a smart list');
    }
    if (list.name === SAVED_EVENTS_LIST_NAME) {
      throw new Error('The "Saved Events" list cannot be edited');
    }

    await db.$transaction([
      ...(data.removeEventIds.length > 0
        ? [
            db.eventListItem.deleteMany({
              where: {
                eventListId: data.listId,
                eventId: {in: data.removeEventIds},
              },
            }),
          ]
        : []),
      ...(data.addEventIds.length > 0
        ? [
            db.eventListItem.createMany({
              data: data.addEventIds.map((eventId) => ({
                eventListId: data.listId,
                eventId,
              })),
              skipDuplicates: true,
            }),
          ]
        : []),
    ]);

    return {success: true};
  });

/**
 * Returns IDs of all lists that contain the given event for the current user.
 * Returns the virtual 'saved' ID when the event is in the Saved Events list.
 */
export const getEventListMembership = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}): Promise<string[]> => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const items = await db.eventListItem.findMany({
      where: {
        eventId,
        eventList: {profileId: profile.id},
      },
      include: {eventList: {select: {id: true, name: true}}},
    });

    return items.map((item) =>
      item.eventList.name === SAVED_EVENTS_LIST_NAME
        ? 'saved'
        : item.eventList.id
    );
  });
